import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { AppCanvas } from "@/components/glass/AppCanvas";
import { forgotPassword } from "@/lib/api";
import orikinWordmark from "@/assets/brand/orikin-wordmark-on-dark.png";
import "@/pages/login.css";

// §v3.pw-reset — self-service "Forgot password?" page (logged-out).
// Asks for an EMAIL or a username and triggers an emailed reset link. The
// server always responds with the same generic message (anti-enumeration), so
// this page never reveals whether an account exists — nor how many accounts
// the identifier matched.
//
// §preauth-identity (2026-08-10) — the field used to say "Username" only.
// Usernames are auto-derived from the signup email with the `@` stripped
// (owner@example.com → ownerexample.com), so an owner cannot guess theirs and
// this page was unusable for the exact person it exists for.

export function ForgotPasswordPage() {
  const [identifier, setIdentifier] = useState("");
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const generic =
      "If that account exists, a password-reset link has been sent to its email address.";
    try {
      const res = await forgotPassword(identifier.trim());
      setMessage(res?.message || generic);
    } catch {
      // Never surface errors here — keep the response indistinguishable.
      setMessage(generic);
    } finally {
      setSent(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="lp-stage">
      <AppCanvas variant="login" />
      <form className="lp-card" onSubmit={handleSubmit} noValidate>
        <header className="lp-head">
          <img
            src={orikinWordmark}
            alt="ORIKIN AI"
            className="lp-wordmark"
            draggable={false}
          />
          <div className="lp-tagline">RESET PASSWORD</div>
        </header>

        <div className="lp-form">
          {sent ? (
            <>
              <div role="status" className="lp-success">
                {message}
              </div>
              <p style={{ fontSize: 13, opacity: 0.7, textAlign: "center" }}>
                Check your inbox (and spam) for the reset link — it expires in
                30 minutes.
              </p>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, opacity: 0.75 }}>
                Enter the email address you signed up with (or your username)
                and we'll send a password-reset link to the address on file.
              </p>
              <div className="lp-field">
                <label htmlFor="identifier" className="lp-label">
                  Email or username
                </label>
                <input
                  id="identifier"
                  name="identifier"
                  autoComplete="username"
                  autoFocus
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com"
                  className="lp-input"
                />
              </div>
              <button
                type="submit"
                className="lp-submit"
                disabled={submitting}
                aria-busy={submitting || undefined}
              >
                {submitting ? (
                  <>
                    <span className="lp-submit-spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  "Send reset link"
                )}
              </button>
            </>
          )}

          <Link
            to="/login"
            className="lp-bootstrap-toggle"
            style={{ textAlign: "center", display: "block" }}
          >
            ← Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}

export default ForgotPasswordPage;
